import { FolderOpen } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { ChainlitContext, useAuth, useChatInteract, useSubscription } from '@chainlit/react-client';

import UpgradeRequiredModal from '@/components/UpgradeRequiredModal';

import Page from 'pages/Page';

import { useTranslation } from '@/components/i18n/Translator';
import { Loader } from '@/components/Loader';
import type { Folder, FolderThread } from '@/api';
import { foldersState } from '@/state/folders';

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const apiClient = useContext(ChainlitContext) as any;
  const [folders] = useRecoilState(foldersState);
  const { t } = useTranslation();
  const { clear } = useChatInteract();
  const { user } = useAuth();
  const { isProUser } = useSubscription();
  const isAdmin =
    (user as any)?.metadata?.role === 'ADMIN' ||
    (user as any)?.role === 'ADMIN';
  const canAccess = isAdmin || isProUser;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [threads, setThreads] = useState<FolderThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState<Folder | null>(null);

  // Find folder from state or fetch
  useEffect(() => {
    if (!projectId) return;
    const found = folders.find((f) => f.id === projectId);
    if (found) {
      setFolder(found);
    } else if (apiClient?.listFolders) {
      apiClient.listFolders().then((f: Folder[]) => {
        const match = f.find((folder) => folder.id === projectId);
        setFolder(match || null);
      });
    }
  }, [projectId, folders]);

  // Fetch threads in this project
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiClient
      .getFolderThreads(projectId)
      .then((t: FolderThread[]) => setThreads(t))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (!projectId) return null;

  const folderName = folder?.name || 'Project';

  return (
    <Page>
      <div className="flex flex-col items-center w-full h-full overflow-auto px-4 py-8">
        <div className="w-full max-w-2xl">
          {/* Project header */}
          <div className="flex items-center gap-3 mb-8">
            <FolderOpen className="h-8 w-8 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">{folderName}</h1>
          </div>

          {/* New chat button — navigates to / with projectId param */}
          <button
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-muted/40 hover:bg-muted/70 transition-colors text-muted-foreground text-left mb-8"
            onClick={() => {
              if (!canAccess) { setShowUpgradeModal(true); return; }
              clear();
              navigate(`/?projectId=${projectId}`);
            }}
          >
            <span className="text-lg font-light">+</span>
            <span>{t('projects.page.newChat', { projectName: folderName })}</span>
          </button>
          <UpgradeRequiredModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />

          {/* Threads list */}
          <div>
            <h2 className="text-sm font-medium text-muted-foreground mb-3">
              {t('projects.page.chats')}
            </h2>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : threads.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('projects.page.empty')}
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {threads.map((thread) => (
                  <Link
                    key={thread.id}
                    to={`/thread/${thread.id}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">
                        {thread.name || 'Untitled'}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 ml-4">
                      {new Date(thread.createdAt).toLocaleDateString(
                        undefined,
                        { month: 'short', day: 'numeric' }
                      )}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
